import { forwardRef, useImperativeHandle, useState } from 'react';
import EventPaymentPanel from './EventPaymentPanel.jsx';
import {
    setPaymentHistory,
    useBusinessInfo,
    usePaymentHistory,
} from '../../lib/Queries';
import { formatDateTimeLong } from '../../lib/DateTime.js';
import { formatCurrency } from '../../lib/CurrencyHelpers.js';
import SectionHeading from '../SectionHeading.jsx';
import PaymentHistory from '../payments/PaymentHistory.jsx';
import { sumProperty, ucfirst } from '../../lib/Utils.js';
import Loading from '../Loading';
import { humanSingularObjectName, ObjectTypes } from '~/lib/ObjectTypes';
import { FEATURE_COUPONS, useFeatures } from '~/lib/Features';
import TextInput from '~/components/TextInput';
import useBusinessPostApi from '~/lib/useBusinessPostApi';
import * as Toast from '~/lib/Toast';
import SendToSquareTerminalForm from '~/components/payments/SendToSquareTerminalForm.jsx';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

const CouponInput = ({ onSuccess, className, type, id }) => {
    const [code, setCode] = useState('');
    const { save, isSaving } = useBusinessPostApi(
        `/payments/coupon/${type}/${id}`,
    );

    function doApply() {
        save({
            code,
        }).then(result => {
            setPaymentHistory(type, id, result);
            onSuccess();
        });
    }

    return (
        <div className={className}>
            <div className="input-label">Coupon Code</div>
            <div className={`flex items-center`}>
                <TextInput
                    onChange={setCode}
                    value={code}
                    id="code"
                    autoFocus={true}
                    className="mr-2"
                />
                <button
                    type="button"
                    className="btn-light-pill"
                    onClick={doApply}
                >
                    {isSaving ? 'Applying...' : 'Apply'}
                </button>
            </div>
        </div>
    );
};

/**
 *
 * @param event Optional, either event or ID and Type are required
 * @param id Optional, if omitted must include event
 * @param type Optional, if omitted must include event
 * @param className
 * @param headingClassName
 * @param refetch
 * @returns {JSX.Element|null}
 * @constructor
 */
const Payments = forwardRef(
    ({ event, id, type, className, headingClassName, refetch }, ref) => {
        const [isOpen, setIsOpen] = useState(false);
        const [isAddingCoupon, setIsAddingCoupon] = useState(false);
        const [showSendToSquare, setShowSendToSquare] = useState(false);
        const { isEnabled } = useFeatures();

        type = type || ObjectTypes.BOOKING_EVENT;
        id = id || event?.id;
        const {
            data,
            isLoading,
            refetch: refetchPaymentHistory,
        } = usePaymentHistory(type, id);
        const { data: businessInfo } = useBusinessInfo();

        function safeRefetch() {
            if (typeof refetch === 'function') {
                refetch();
            }
        }

        useImperativeHandle(ref, () => ({
            refetch: refetchPaymentHistory,
            makePayment: () => setIsOpen(true),
        }));

        const { save: saveRemoveCoupons, isSaving: isRemovingCoupons } =
            useBusinessPostApi(`/payments/coupon/${type}/${id}/remove`);

        const history = data?.payments || [];
        const totalPaid = sumProperty(history, 'amount');
        const humanType = humanSingularObjectName(type);

        /**
         * Money paid on the form submission that isn't recorded as a payment here.
         * Add-ons and payment questions don't have an object of their own to record a
         *  payment against, so without this the list adds up to less than what was paid.
         */
        const submissionItems = (data?.submissionPayments || []).map(
            payment => {
                const processorName =
                    payment.processor === 'giftcertificate'
                        ? 'Gift Certificate'
                        : ucfirst(payment.processor);

                return {
                    key: `submission-payment-${payment.id}`,
                    created_at: payment.created_at,
                    icon: (
                        <InformationCircleIcon className="text-primary-700" />
                    ),
                    title: 'Submission Add-Ons',
                    amount: payment.amount,
                    description: (
                        <div>
                            <div>{formatDateTimeLong(payment.created_at)}</div>
                            <div>
                                Part of a {formatCurrency(payment.total_amount)}{' '}
                                payment made when the form was submitted
                                {payment.applied_here > 0 &&
                                    ` (${formatCurrency(payment.applied_here)} of it applied to this ${humanType})`}
                            </div>
                            <div>
                                {payment.external_link ? (
                                    <>
                                        via{' '}
                                        <a
                                            href={payment.external_link}
                                            target="_blank"
                                            className="text-gray-500"
                                        >
                                            {processorName}
                                        </a>
                                    </>
                                ) : (
                                    `via ${processorName}`
                                )}
                            </div>
                        </div>
                    ),
                };
            },
        );

        const canAddCoupon = isEnabled(FEATURE_COUPONS) && !data?.codes?.length;
        const amountOwed = event?.price - totalPaid;
        const canSendToSquare =
            businessInfo?.canPay &&
            businessInfo?.terminalDevices?.length > 0 &&
            amountOwed > 0;

        if (
            !businessInfo?.canPay &&
            history.length === 0 &&
            !!event &&
            !event?.price
        ) {
            return null;
        }

        return (
            <>
                <SectionHeading
                    icon={'$'}
                    name="Payments"
                    className={headingClassName}
                />
                <div className={`mt-2 ${className}`}>
                    {!isLoading && (
                        <>
                            <PaymentHistory
                                history={history}
                                extraItems={submissionItems}
                                codes={data?.codes || []}
                                savedPaymentMethods={data?.savedPaymentMethods}
                                totalPrice={event?.price || -1}
                                label={submissionItems?.length > 0 ? `${humanType} (without add-ons)` : humanType}
                                onUpdate={() => {
                                    safeRefetch();
                                    refetchPaymentHistory();
                                }}
                                isRemovingCoupons={isRemovingCoupons}
                                onRemoveCoupons={() => {
                                    saveRemoveCoupons().then(result => {
                                        safeRefetch();
                                        setPaymentHistory(type, id, result);
                                        Toast.success('Removed coupons');
                                    });
                                }}
                            />
                            {data?.submissionTotal > 0 && (
                                <div className="my-2 ml-4 text-sm text-gray-500">
                                    Total for all items in submission, including
                                    add-ons:{' '}
                                    {formatCurrency(data?.submissionTotal)}.
                                    {data?.submissionRemaining <
                                        data?.submissionTotal && (
                                        <div>
                                            There is a{' '}
                                            {formatCurrency(
                                                data?.submissionRemaining,
                                            )}{' '}
                                            total balance remaining.
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                    {isLoading && (
                        <div className="panel p-4 mt-2">
                            <Loading />
                        </div>
                    )}

                    {isAddingCoupon && (
                        <CouponInput
                            onSuccess={() => {
                                safeRefetch();
                                setIsAddingCoupon(false);
                            }}
                            type={type}
                            id={id}
                            className="mt-2"
                        />
                    )}

                    {!!event && (
                        <>
                            <div className="flex items-center mt-2">
                                <button
                                    type="button"
                                    className="btn-light-pill mr-3"
                                    onClick={() => setIsOpen(true)}
                                >
                                    Make a Payment
                                </button>
                                {canSendToSquare && (
                                    <button
                                        type="button"
                                        className="btn-light-pill"
                                        onClick={() =>
                                            setShowSendToSquare(true)
                                        }
                                    >
                                        Square Terminal Checkout
                                    </button>
                                )}
                                {canAddCoupon && (
                                    <button
                                        type="button"
                                        className="btn-light-pill"
                                        onClick={() => setIsAddingCoupon(true)}
                                    >
                                        Add Coupon
                                    </button>
                                )}
                            </div>

                            {showSendToSquare && (
                                <SendToSquareTerminalForm
                                    type={ObjectTypes.BOOKING_EVENT}
                                    id={event.id}
                                    amount={amountOwed}
                                />
                            )}

                            <EventPaymentPanel
                                isOpen={isOpen}
                                onClose={() => setIsOpen(false)}
                                event={event}
                                totalPaid={
                                    data?.submissionTotalPaid || totalPaid
                                }
                                totalPrice={
                                    data?.submissionTotal || event?.price
                                }
                                refetch={refetch}
                            />
                        </>
                    )}
                </div>
            </>
        );
    },
);

export default Payments;
